// Hoort wanneer de respondent praat, en vooral wanneer hij klaar is.
//
// Bij de vorige motor deed OpenAI dit op de server. Nu moet het op het apparaat.
// Bewust op geluidsniveau en niet met een spraakmodel: dat zou een bibliotheek
// plus een model van een paar megabyte kosten dat de respondent moet downloaden
// op een pagina zonder login. De prijs is dat hard achtergrondgeluid als spraak
// kan tellen.
//
// Twee dingen maken het bruikbaar in een echte kamer:
// - de drempel schuift mee met het achtergrondniveau, want een vaste drempel
//   werkt alleen in een stille ruimte;
// - de opname bewaart een stukje van vóór het begin, want zonder dat aanloopje
//   mist Whisper de eerste lettergreep en dus vaak het woord.
//
// AI-CONTEXT: See docs/features/ai-interviewer-ovh-stt-tts.md

/** Hoe lang stil voordat we de beurt als afgelopen zien. Korter valt in de rede. */
const SILENCE_MS = 700
/** Korter dan dit is een kuchje of een deur, geen antwoord. */
const MIN_SPEECH_MS = 250
/** Zoveel geluid van vóór het begin gaat mee de opname in. */
const PREROLL_MS = 300
/** Hoeveel harder dan de achtergrond het moet zijn om als spraak te tellen. */
const SPEECH_FACTOR = 3.5
/** Ondergrens, zodat in een doodstille kamer niet elk ruisje spraak wordt. */
const FLOOR_MIN = 0.004;
/**
 * Zo lang luisteren we alleen mee om te horen hoe stil de kamer is, zonder te
 * oordelen. Zonder dit ijkmoment beslist de detector bij het éérste blokje, en
 * dan is in een rumoerige ruimte het geroezemoes zelf al 'spraak' — de beurt
 * begint dan meteen en eindigt nooit.
 */
const CALIBRATION_MS = 500;
/**
 * Langer duurt één beurt nooit. In een rumoerige ruimte wordt het soms nooit
 * stil genoeg; zonder grens groeit de opname dan tot de server hem weigert
 * (functions/aiInterviewer/gate.js, maxClipSeconds: 120). Ruim daaronder, want
 * het aanloopje komt er nog bij.
 */
const MAX_TURN_MS = 110000;

/**
 * Het effectieve geluidsniveau van een blokje (root mean square).
 *
 * @param {Float32Array} frame
 * @returns {number} 0 bij stilte, richting 1 bij vol geluid
 */
const rms = (frame) => {
    if (!frame.length) return 0;
    let sum = 0;
    for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
    return Math.sqrt(sum / frame.length);
};

/**
 * @typedef {object} SpeechDetector
 * @property {(frame: Float32Array) => void} push - Voer een blokje microfoongeluid in
 * @property {() => void} pause - Even niet luisteren
 * @property {() => void} resume - Weer luisteren
 * @property {() => boolean} isSpeaking - Praat de respondent nu
 */

/**
 * Maakt een detector die spraak van stilte onderscheidt.
 *
 * @param {object} options
 * @param {number} options.sampleRate - Samplerate van de blokjes
 * @param {(samples: Float32Array) => void} [options.onSpeechEnd] - Krijgt de hele opname
 * @param {() => void} [options.onSpeechStart] - Voor het onderbreken van de stem
 * @param {number} [options.silenceMs]
 * @param {number} [options.minSpeechMs]
 * @returns {SpeechDetector}
 */
const createSpeechDetector = ({
    sampleRate,
    onSpeechEnd,
    onSpeechStart,
    silenceMs = SILENCE_MS,
    minSpeechMs = MIN_SPEECH_MS,
    calibrationMs = CALIBRATION_MS,
}) => {
    /** @type {Float32Array[]} */
    let opname = [];
    /** @type {Float32Array[]} Ringbuffer met geluid van vlak vóór het begin. */
    let preroll = [];
    let prerollSamples = 0;
    let praat = false;
    let spraakMs = 0;
    let stilteMs = 0;
    let beurtMs = 0;
    let actief = true;
    // Achtergrondniveau; start hoog genoeg om het eerste blokje niet meteen
    // als spraak te zien, en zakt daarna naar wat de kamer werkelijk doet.
    let achtergrond = FLOOR_MIN;
    let geijktMs = 0;

    const prerollLimiet = (PREROLL_MS / 1000) * sampleRate;

    const sluitAf = () => {
        const stukken = [...preroll, ...opname];
        const totaal = stukken.reduce((n, s) => n + s.length, 0);
        const samples = new Float32Array(totaal);
        let at = 0;
        for (const stuk of stukken) { samples.set(stuk, at); at += stuk.length; }

        praat = false;
        spraakMs = 0;
        stilteMs = 0;
        beurtMs = 0;
        opname = [];
        preroll = [];
        prerollSamples = 0;
        if (samples.length) onSpeechEnd?.(samples);
    };

    return {
        push: (binnen) => {
            if (!actief) return;
            // Kopiëren, want de browser vult bij elk blokje dezelfde array
            // opnieuw. Met alleen een verwijzing bestaat de opname aan het eind
            // uit de laatste stilte, herhaald, en verstaat Whisper niets.
            const frame = new Float32Array(binnen);
            const ms = (frame.length / sampleRate) * 1000;
            const niveau = rms(frame);

            // IJken: alleen meten hoe stil de kamer is, nog niet oordelen.
            if (geijktMs < calibrationMs) {
                geijktMs += ms;
                // Snel volgen, want we hebben maar een halve seconde.
                achtergrond = achtergrond * 0.7 + niveau * 0.3;
                return;
            }

            const drempel = Math.max(achtergrond * SPEECH_FACTOR, FLOOR_MIN);
            const luid = niveau > drempel;

            if (praat) {
                opname.push(frame);
                beurtMs += ms;
                if (beurtMs >= MAX_TURN_MS) {
                    sluitAf();
                    return;
                }
                if (luid) {
                    spraakMs += ms;
                    stilteMs = 0;
                } else {
                    stilteMs += ms;
                    if (stilteMs >= silenceMs) {
                        // Te kort om een antwoord te zijn: weggooien zonder melding.
                        if (spraakMs < minSpeechMs) {
                            praat = false;
                            spraakMs = 0;
                            stilteMs = 0;
                            beurtMs = 0;
                            opname = [];
                            return;
                        }
                        sluitAf();
                    }
                }
                return;
            }

            // Nog niet aan het praten: het achtergrondniveau bijstellen en een
            // aanloopje bewaren.
            if (!luid) {
                // Langzaam volgen, zodat één harde klap de drempel niet meeneemt.
                achtergrond = achtergrond * 0.95 + niveau * 0.05;
            }
            preroll.push(frame);
            prerollSamples += frame.length;
            while (prerollSamples > prerollLimiet && preroll.length > 1) {
                prerollSamples -= preroll[0].length;
                preroll.shift();
            }

            if (luid) {
                spraakMs += ms;
                if (spraakMs >= minSpeechMs / 2) {
                    praat = true;
                    stilteMs = 0;
                    onSpeechStart?.();
                }
            } else {
                spraakMs = 0;
            }
        },

        pause: () => { actief = false; },
        resume: () => { actief = true; },
        isSpeaking: () => praat,
    };
};

export { rms, SILENCE_MS, MIN_SPEECH_MS, PREROLL_MS, CALIBRATION_MS, MAX_TURN_MS };
export default createSpeechDetector;
